
/* drag to scroll: every sideways row can be pulled with the mouse, the way a finger moves it on a phone.
   On release the row keeps some of the throw and comes to rest on a snap point; a drag never counts as a click.
   pdp3 (2026-09-26, CEO: a mouse throw could leave the product photos stopped between two): photos hidden by a metal
   choice (display:none) no longer count as snap points (their empty box sat at the row's left edge and offered a stop
   mid-way), and a glide that ends off a snap point is finished onto the nearest one. Photo sliders also move one photo
   per arrow key (Home and End: first and last). */
(() => {
  const SEL = '.hh-sym-scroller, .hh-cats, .hh-grid, .hh-reviews, .hh-gifts, .hh-journal, .hh-tabs, .media-viewer, .media-thumbs, .slider';
  let el = null, x0 = 0, s0 = 0, moved = false, eat = false, trail = [];
  const velocity = () => {   // px per ms over the last 100 ms of the drag
    const now = performance.now(), t = trail.filter(p => now - p[0] < 100);
    return t.length < 2 ? 0 : (t[t.length - 1][1] - t[0][1]) / Math.max(1, t[t.length - 1][0] - t[0][0]);
  };
  // the row's snap positions (children that take up no room, e.g. photos of another metal, are not stops)
  function snapPoints(t) {
    const max = t.scrollWidth - t.clientWidth, sr = t.getBoundingClientRect(), cs = getComputedStyle(t), out = [];
    if (!cs.scrollSnapType || cs.scrollSnapType === 'none') return out;
    const pl = parseFloat(cs.scrollPaddingLeft) || 0, pr = parseFloat(cs.scrollPaddingRight) || 0;
    for (const c of t.children) {
      const r = c.getBoundingClientRect();
      if (!r.width) continue;
      const al = getComputedStyle(c).scrollSnapAlign.split(' ').pop();
      if (!al || al === 'none') continue;
      const left = t.scrollLeft + r.left - sr.left;
      const at = al === 'center' ? left + r.width / 2 - t.clientWidth / 2 : al === 'end' ? left + r.width - t.clientWidth + pr : left - pl;
      out.push(Math.round(Math.max(0, Math.min(max, at))));
    }
    return [...new Set(out)].sort((a, b) => a - b);
  }
  // where the browser would snap near `x`: the closest snap position of the row's children
  function snapNear(t, x) {
    const max = t.scrollWidth - t.clientWidth;
    x = Math.max(0, Math.min(max, x));
    let best = x, d = Infinity;
    for (const v of snapPoints(t)) if (Math.abs(v - x) < d) { d = Math.abs(v - x); best = v; }
    return best;
  }
  document.addEventListener('pointerdown', e => {
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    const t = e.target.closest(SEL);
    if (!t || t.scrollWidth <= t.clientWidth + 2 || t.closest('dialog')) return;
    el = t; x0 = e.clientX; s0 = t.scrollLeft; moved = false; trail = [[performance.now(), e.clientX]];
  });
  addEventListener('pointermove', e => {
    if (!el) return;
    const dx = e.clientX - x0;
    if (!moved) { if (Math.abs(dx) < 6) return; moved = true; el.classList.add('is-dragging', 'is-gliding'); }
    el.scrollLeft = s0 - dx;
    trail.push([performance.now(), e.clientX]); if (trail.length > 12) trail.shift();
  });
  const end = () => {
    if (!el) return;
    const t = el; el = null;
    if (!moved) return;
    eat = true; setTimeout(() => { eat = false; }, 0);
    const to = snapNear(t, t.scrollLeft - velocity() * 280);
    t.classList.remove('is-dragging');
    t.scrollTo({ left: to, behavior: 'smooth' });
    // snapping stays off until the glide ends on the snap point, so the browser has nothing to correct
    let done = false;
    const settle = () => {
      if (done) return; done = true; t.classList.remove('is-gliding');
      // finish on a stop if the glide was cut short (another scroll, a layout change)
      const at = snapNear(t, t.scrollLeft);
      if (Math.abs(at - t.scrollLeft) > 1) t.scrollTo({ left: at, behavior: 'smooth' });
    };
    t.addEventListener('scrollend', settle, { once: true }); setTimeout(settle, 900);
  };
  addEventListener('pointerup', end); addEventListener('pointercancel', end);
  document.addEventListener('click', e => { if (eat) { e.preventDefault(); e.stopImmediatePropagation(); eat = false; } }, true);
  document.addEventListener('dragstart', e => { if (e.target.closest && e.target.closest(SEL)) e.preventDefault(); });
  // photo sliders: one photo per arrow key, Home and End for the first and last
  document.addEventListener('keydown', e => {
    if (e.altKey || e.ctrlKey || e.metaKey || !['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
    const t = e.target instanceof Element && e.target.closest('.media-viewer');
    if (!t || t.closest('dialog') || t.scrollWidth <= t.clientWidth + 2) return;
    const pts = snapPoints(t);
    if (pts.length < 2) return;
    const x = t.scrollLeft;
    const to = e.key === 'Home' ? pts[0] : e.key === 'End' ? pts[pts.length - 1]
      : e.key === 'ArrowRight' ? (pts.find(v => v > x + 2) ?? pts[pts.length - 1]) : ([...pts].reverse().find(v => v < x - 2) ?? pts[0]);
    e.preventDefault();
    t.scrollTo({ left: to, behavior: matchMedia('(prefers-reduced-motion:reduce)').matches ? 'auto' : 'smooth' });
  });
})();

/* product photos, full screen (takes over from the theme's zoom).
   Drag or swipe sideways for the next photo, pull up or down to close, ← → and Esc on the keyboard.
   Desktop: click to zoom where you point, the photo then follows the mouse. Touch: double-tap or pinch to zoom, drag to pan.
   Opening grows the photo out of the one on the page, closing puts it back there.
   pdp3 (2026-09-26, CEO: shoppers did not see there were more photos): arrows at the photo's sides on phones too (44px,
   off at the first and last photo, no loop), and at the bottom, in place of the thumbnails, the site's pagination: the
   phone gallery's segmented line (one segment per photo, tap or slide along it to jump) over the collection's count line
   ("Photo 3 of 8"). Swipes, arrows, keys and the bar all go through go(), so they stay in step.
   pdpvideo (2026-09-29, CEO: "tapping the first photo and going left/right, why doesn't the video come?"): the product
   video (the page's .hp3-vid, assets/heon-pdp-video.js) is in the viewer too, in its place on the page (2nd). It plays
   muted and looping while it is the one on screen; a tap, a click, Enter or Space stops it and another starts it again
   (reduced motion: its first frame until then). It does not zoom; swipes, the pull to close and the arrows work as on a
   photo. Leaving it or closing the viewer stops it. */
(() => {
  const REDUCE = matchMedia('(prefers-reduced-motion:reduce)').matches;
  const T = (k, d) => { const s = window.HeonI18n && window.HeonI18n[k]; return typeof s === 'string' && s ? s : d; };   // the page's language (snippets/heon-i18n-js), else the English default
  const EASE = 'cubic-bezier(.2,.7,.2,1)', ZOOM = 2.4;
  const sized = (u, w) => /[?&]width=\d+/.test(u) ? u.replace(/([?&])width=\d+/, `$1width=${w}`) : `${u}${u.includes('?') ? '&' : '?'}width=${w}`;
  let lb, track, count, dots, prev, next, pics = [], i = 0, open = false, closing = false, W = 0, H = 0, top = 0, bottom = 0;

  function build() {
    lb = document.createElement('dialog');
    lb.className = 'hh-lb'; lb.setAttribute('aria-label', T('media_label', 'Product photos'));
    lb.innerHTML = `<div class="hh-lb-stage"><div class="hh-lb-track"></div></div>
      <div class="hh-lb-bar">
        <button class="hh-lb-close" type="button"><span>${T('close', 'Close')}</span><svg viewBox="0 0 24 24" aria-hidden="true"><path d="M5.5 5.5l13 13M18.5 5.5l-13 13"/></svg></button></div>
      <button class="hh-lb-nav hh-lb-prev" type="button" aria-label="${T('media_prev', 'Previous photo')}"><svg viewBox="0 0 12 20" aria-hidden="true"><path d="M10 2 2 10l8 8"/></svg></button>
      <button class="hh-lb-nav hh-lb-next" type="button" aria-label="${T('media_next', 'Next photo')}"><svg viewBox="0 0 12 20" aria-hidden="true"><path d="m2 2 8 8-8 8"/></svg></button>
      <div class="hh-lb-pag"><div class="hh-lb-dots" role="group" aria-label="${T('media_choose', 'Choose a photo')}"></div>
        <p class="hh-lb-count" aria-live="polite"></p></div>`;
    document.body.appendChild(lb);
    track = lb.querySelector('.hh-lb-track'); count = lb.querySelector('.hh-lb-count'); dots = lb.querySelector('.hh-lb-dots');
    prev = lb.querySelector('.hh-lb-prev'); next = lb.querySelector('.hh-lb-next');
    lb.querySelector('.hh-lb-close').addEventListener('click', () => shut());
    prev.addEventListener('click', () => go(i - 1));
    next.addEventListener('click', () => go(i + 1));
    // the bar: a click or Enter on a segment, or press and slide along it (the segments are narrow on a phone)
    dots.addEventListener('click', e => { const b = e.target.closest('button'); if (b) go(+b.dataset.i); });
    const under = x => { const bs = [...dots.children]; const k = bs.findIndex(b => x < b.getBoundingClientRect().right + 2); return k < 0 ? bs.length - 1 : k; };
    let scrub = null;
    dots.addEventListener('pointerdown', e => {
      if (e.button > 0 || !dots.children.length) return;
      scrub = e.pointerId; dots.setPointerCapture(e.pointerId); go(under(e.clientX));
    });
    dots.addEventListener('pointermove', e => { if (scrub === e.pointerId) { const k = under(e.clientX); if (k !== i) go(k); } });
    const stop = e => { if (scrub === e.pointerId) scrub = null; };
    dots.addEventListener('pointerup', stop); dots.addEventListener('pointercancel', stop);
    lb.addEventListener('cancel', e => { e.preventDefault(); shut(); });
    lb.addEventListener('close', cleanup);
    lb.addEventListener('keydown', e => {
      if ((e.key === ' ' || e.key === 'Enter') && pics[i] && pics[i].vid && (e.target === pics[i].box || e.target === lb)) { e.preventDefault(); flip(pics[i]); return; }
      const k = { ArrowRight: i + 1, ArrowLeft: i - 1, Home: 0, End: pics.length - 1 }[e.key];
      if (k !== undefined) { e.preventDefault(); go(k); }
    });
    gestures(lb.querySelector('.hh-lb-stage'));
    // the stage never scrolls, only the track moves; a focus that scrolled it (pdpvideo: the video's box) is undone
    lb.querySelector('.hh-lb-stage').addEventListener('scroll', e => { e.target.scrollLeft = 0; e.target.scrollTop = 0; });
    addEventListener('resize', () => { if (open) { reset(pics[i], false); layout(); place(false); } });
  }

  // the photos of the clicked gallery, in page order, without hidden (other colour) or repeated ones; the product video
  // keeps its place among them
  function collect(gallery) {
    const seen = new Set();
    return [...gallery.querySelectorAll('.media-viewer__item[data-media-type="image"], .media-viewer__item[data-media-type="video"]')].filter(li => {
      const im = li.dataset.mediaType === 'video' ? li.querySelector('video.hp3-vid__v') : li.querySelector('img'), key = li.dataset.mediaId || (im && im.src);
      if (!im || !key || seen.has(key) || li.hidden || getComputedStyle(li).display === 'none') return false;
      seen.add(key); return true;
    });
  }
  function fill(items) {
    track.innerHTML = ''; dots.innerHTML = '';
    pics = items.map((li, k) => {
      const pv = li.querySelector('video.hp3-vid__v');
      if (pv) return film(li, pv, k, items.length);
      const a = li.querySelector('a.js-zoom-link'), im = li.querySelector('img.product-image') || li.querySelector('img');
      const url = (a && (a.dataset.zoomSrc || a.getAttribute('href'))) || im.currentSrc || im.src;
      const slide = document.createElement('div'); slide.className = 'hh-lb-slide';
      const img = document.createElement('img'); img.alt = im.alt || ''; img.decoding = 'async'; img.draggable = false;
      // the photo sits in a linen box like the one on the page, so white backgrounds stay linen while it moves or fades
      const box = document.createElement('div'); box.className = 'hh-lb-ph'; box.appendChild(img);
      slide.appendChild(box); track.appendChild(slide);
      const b = document.createElement('button'); b.type = 'button'; b.className = 'hh-lb-dot'; b.dataset.i = k;
      b.setAttribute('aria-label', T('media_count', 'Photo [k] of [n]').replace('[k]', k + 1).replace('[n]', items.length)); dots.appendChild(b);
      return { li, im, img, box, url, ratio: (+im.getAttribute('width') || 1) / (+im.getAttribute('height') || 1), z: 1, tx: 0, ty: 0, src: false, hi: false };
    });
    lb.classList.toggle('is-single', pics.length < 2);
  }
  // the product video's slide: its first frame (the page's poster, sharper) until it plays; a button for the keyboard
  function film(li, pv, k, n) {
    const slide = document.createElement('div'); slide.className = 'hh-lb-slide';
    const box = document.createElement('div'); box.className = 'hh-lb-ph hh-lb-vid'; box.setAttribute('role', 'button');
    const vid = document.createElement('video');
    vid.muted = vid.defaultMuted = vid.loop = vid.playsInline = true;
    ['muted', 'playsinline', 'disablepictureinpicture', 'disableremoteplayback'].forEach(a => vid.setAttribute(a, ''));
    vid.setAttribute('aria-hidden', 'true'); vid.preload = 'none';
    const po = pv.getAttribute('poster'); if (po) vid.poster = sized(po, 1280);
    const pm = document.createElement('span'); pm.className = 'hh-lb-play'; pm.setAttribute('aria-hidden', 'true');
    pm.innerHTML = '<svg viewBox="0 0 24 24"><path d="M8 5.5v13l10.5-6.5z"/></svg>';
    box.append(vid, pm); slide.appendChild(box); track.appendChild(slide);
    const b = document.createElement('button'); b.type = 'button'; b.className = 'hh-lb-dot'; b.dataset.i = k;
    b.setAttribute('aria-label', T('media_count', 'Photo [k] of [n]').replace('[k]', k + 1).replace('[n]', n)); dots.appendChild(b);
    const tb = li.querySelector('.hp3-vid__t'), say = { pause: (tb && tb.dataset.pause) || 'Pause video', play: (tb && tb.dataset.play) || 'Play video' };
    return { li, im: pv, vid, box, say, srcs: [...pv.querySelectorAll('source')], ratio: (+pv.getAttribute('width') || 1) / (+pv.getAttribute('height') || 1),
      z: 1, tx: 0, ty: 0, src: false, hi: true, stop: REDUCE };
  }
  // only the video on screen plays, while the viewer is open and the shopper has not stopped it. Only then can it take
  // the focus (no tabindex at all before: showModal() focuses even a tabindex=-1 box, and a box off screen that takes
  // the focus scrolls the stage); if it had the focus when it leaves the screen, the arrow takes it (keys keep working)
  function films() {
    pics.forEach((p, j) => {
      if (!p.vid) return;
      if (open && j === i) p.box.tabIndex = 0;
      else if (p.box.hasAttribute('tabindex')) {
        if (document.activeElement === p.box) (open ? (next.disabled ? prev : next) : lb).focus({ preventScroll: true });
        p.box.removeAttribute('tabindex');
      }
      const on = open && !closing && j === i && !p.stop;
      if (on && p.vid.paused) {
        const r = p.vid.play();
        if (r && r.catch) r.catch(err => { if (err && err.name === 'NotAllowedError' && p.vid.paused) { p.stop = true; mark(p); } });
      } else if (!on && !p.vid.paused) p.vid.pause();
      mark(p);
    });
  }
  const mark = p => { p.box.classList.toggle('is-paused', !!p.stop); p.box.setAttribute('aria-label', p.stop ? p.say.play : p.say.pause); };
  const flip = p => { p.stop = !(p.stop || p.vid.paused); films(); };

  // pictures load for the photo on screen and its two neighbours; the page's own copy shows until the sharp one is ready
  function ensure(k) {
    const p = pics[k]; if (!p || p.src) return; p.src = true;
    if (p.vid) { p.srcs.forEach(s => p.vid.appendChild(s.cloneNode())); p.vid.preload = 'metadata'; p.vid.load(); return; }
    const ready = p.im.complete && p.im.naturalWidth ? p.im.currentSrc || p.im.src : '';
    const big = sized(p.url, 1600);
    if (!ready) { p.img.src = big; return; }
    p.img.src = ready;
    const n = new Image(); n.src = big; n.decode().then(() => { if (!p.hi) p.img.src = big; }).catch(() => {});
  }

  // every photo box is sized to fit the stage, so it is exactly the visible photo
  function layout() {
    W = innerWidth; H = innerHeight;
    const small = W <= 760, many = pics.length > 1, sideX = small ? 0 : 104;
    top = small ? 56 : 76; bottom = many ? (small ? 88 : 108) : (small ? 32 : 56);
    lb.style.setProperty('--lb-top', top + 'px'); lb.style.setProperty('--lb-bottom', bottom + 'px');
    const bw = W - 2 * sideX, bh = H - top - bottom;
    pics.forEach(p => {
      let w = bw, h = w / p.ratio;
      if (h > bh) { h = bh; w = h * p.ratio; }
      p.w = w; p.h = h; p.box.style.width = w + 'px'; p.box.style.height = h + 'px';
    });
  }
  const midY = () => top + (H - top - bottom) / 2;   // the photo's centre on the stage, before any zoom

  // row of photos
  const setTrack = (x, anim) => { track.style.transition = anim ? `transform .52s ${EASE}` : 'none'; track.style.transform = `translate3d(${x}px,0,0)`; };
  const place = anim => setTrack(-i * W, anim && !REDUCE);

  // zoom: translate then scale about the photo's centre; a zoomed photo always covers the screen edges it passes
  function paint(p, anim) {
    p.box.style.transition = anim && !REDUCE ? `transform .45s ${EASE}` : 'none';
    p.box.style.transform = p.z === 1 && !p.tx && !p.ty ? '' : `translate3d(${p.tx}px,${p.ty}px,0) scale(${p.z})`;
    lb.classList.toggle('is-zoomed', p.z > 1);
  }
  function clamp(p) {
    const w = p.w * p.z, h = p.h * p.z, cy = midY(), cx = W / 2;
    p.tx = w <= W ? 0 : Math.min(w / 2 - cx, Math.max(W - cx - w / 2, p.tx));
    p.ty = p.z === 1 ? 0 : h <= H ? H / 2 - cy : Math.min(h / 2 - cy, Math.max(H - cy - h / 2, p.ty));
  }
  function sharpen(p) {
    if (p.hi) return; p.hi = true;
    const n = new Image(); n.src = sized(p.url, 2600); n.decode().then(() => { p.img.src = n.src; }).catch(() => {});
  }
  function follow(p, x, y) {   // desktop: where the pointer is on the screen picks the part of the photo shown
    const w = p.w * p.z, h = p.h * p.z, cy = midY(), cx = W / 2;
    p.tx = w / 2 - cx + (x / W) * (W - w); p.ty = h / 2 - cy + (y / H) * (H - h);
    clamp(p);
  }
  function zoomAt(p, z, x, y, anim = true) {   // keep the point under the finger where it is
    const cx = W / 2, cy = midY(), ux = (x - cx - p.tx) / p.z, uy = (y - cy - p.ty) / p.z;
    p.z = z; p.tx = x - cx - z * ux; p.ty = y - cy - z * uy;
    clamp(p); paint(p, anim); if (z > 1) sharpen(p);
  }
  function reset(p, anim = true) { if (p && (p.z !== 1 || p.tx || p.ty)) { p.z = 1; p.tx = p.ty = 0; paint(p, anim); } }

  function go(k, anim = true) {
    if (!pics.length) return;
    const n = Math.max(0, Math.min(pics.length - 1, k));
    if (n !== i) reset(pics[i], false);
    i = n; place(anim);
    [i, i + 1, i - 1].forEach(ensure);
    count.textContent = pics.length > 1 ? T('media_count', 'Photo [k] of [n]').replace('[k]', i + 1).replace('[n]', pics.length) : '';
    [...dots.children].forEach((b, j) => { b.classList.toggle('is-on', j === i); j === i ? b.setAttribute('aria-current', 'true') : b.removeAttribute('aria-current'); });
    lb.classList.toggle('is-first', i === 0); lb.classList.toggle('is-last', i === pics.length - 1);
    // no loop: the arrow at an end is off; if it had the focus, the other arrow takes it (keys keep working)
    const offP = i === 0, offN = i === pics.length - 1;
    if (offP && !offN && document.activeElement === prev) next.focus();
    if (offN && !offP && document.activeElement === next) prev.focus();
    prev.disabled = offP; next.disabled = offN;
    films();
  }

  // the transform that lays the lightbox photo exactly over a rectangle on the page
  const over = (p, r) => `translate3d(${r.left + r.width / 2 - W / 2}px,${r.top + r.height / 2 - midY()}px,0) scale(${r.width / p.w})`;
  const onScreen = r => r.width > 0 && r.bottom > 0 && r.top < innerHeight && r.right > 0 && r.left < innerWidth;

  function show(gallery, li) {
    if (!lb) build();
    const items = collect(gallery); if (!items.length) return;
    fill(items);
    lb.classList.remove('is-closing', 'is-zoomed', 'is-dragging');
    lb.style.setProperty('--lb-fade', REDUCE ? 1 : 0);
    lb.showModal(); open = true; closing = false;
    layout(); i = -1; go(Math.max(0, items.indexOf(li)), false);
    lb.getBoundingClientRect();
    lb.style.setProperty('--lb-fade', 1);
    const p = pics[i], r = p.im.getBoundingClientRect();
    if (!REDUCE && onScreen(r)) p.box.animate([{ transform: over(p, r) }, { transform: 'none' }], { duration: 520, easing: EASE });
  }

  function shut(dir = 0) {
    if (!open || closing) return; closing = true;
    const p = pics[i];
    // the page gallery moves to the photo that was last looked at, so closing lands on it
    const vw = p.li.closest('.media-viewer');
    if (vw && vw.scrollWidth > vw.clientWidth + 2) {
      const b = vw.style.scrollBehavior; vw.style.scrollBehavior = 'auto';
      vw.scrollLeft += p.li.getBoundingClientRect().left - vw.getBoundingClientRect().left;
      vw.style.scrollBehavior = b;
    }
    const r = p.im.getBoundingClientRect(), from = p.box.style.transform || 'none', back = onScreen(r);
    films();
    lb.classList.remove('is-dragging'); lb.classList.add('is-closing'); lb.style.setProperty('--lb-fade', 0);
    if (REDUCE) return lb.close();
    p.box.style.transition = 'none';
    const to = back ? [{ transform: from }, { transform: over(p, r) }]
      : [{ transform: from, opacity: 1 }, { transform: `translate3d(0,${(dir || 1) * H * .6}px,0) scale(.8)`, opacity: 0 }];
    p.box.animate(to, { duration: back ? 420 : 300, easing: EASE, fill: 'forwards' }).onfinish = () => lb.close();
  }
  function cleanup() {
    open = false; closing = false; films();
    lb.classList.remove('is-closing', 'is-zoomed', 'is-dragging');
    pics.forEach(p => { p.box.getAnimations().forEach(a => a.cancel()); p.z = 1; p.tx = p.ty = 0; p.box.style.transform = ''; p.box.style.transition = ''; });
    const a = pics[i] && pics[i].li.querySelector('a, [tabindex], .hp3-vid__t');
    a && a.focus({ preventScroll: true });
  }

  // one set of pointer handlers for mouse, pen and touch: sideways = browse, up or down = close, zoomed = pan
  function gestures(stage) {
    const pts = new Map();
    let mode = null, x0 = 0, y0 = 0, trail = [], pinch = null, pan = null, lastTap = 0, wheelSum = 0, wheelLock = false, wheelT;
    const speed = () => {   // px per ms over the last 100 ms
      const now = performance.now(), t = trail.filter(s => now - s[0] < 100);
      if (t.length < 2) return [0, 0];
      const a = t[0], b = t[t.length - 1], dt = Math.max(1, b[0] - a[0]);
      return [(b[1] - a[1]) / dt, (b[2] - a[2]) / dt];
    };
    stage.addEventListener('pointerdown', e => {
      if (e.button > 0 || closing) return;
      stage.setPointerCapture(e.pointerId); pts.set(e.pointerId, [e.clientX, e.clientY]);
      const p = pics[i];
      if (pts.size === 2) {
        if (p.vid) {   // no zoom on the video: a drag the first finger began goes back
          mode = null; setTrack(-i * W, true); p.box.style.transition = ''; p.box.style.transform = '';
          lb.style.setProperty('--lb-fade', 1); lb.classList.remove('is-dragging'); return;
        }
        const [a, b] = [...pts.values()];
        pinch = { d: Math.hypot(a[0] - b[0], a[1] - b[1]) || 1, z: p.z, tx: p.tx, ty: p.ty, mx: (a[0] + b[0]) / 2, my: (a[1] + b[1]) / 2 };
        mode = 'pinch'; setTrack(-i * W, true); lb.classList.add('is-dragging'); return;
      }
      if (pts.size > 2) return;
      mode = e.pointerType === 'mouse' && p.z > 1 ? 'follow' : 'wait';
      x0 = e.clientX; y0 = e.clientY; trail = [[performance.now(), x0, y0]]; pan = { tx: p.tx, ty: p.ty };
    });
    stage.addEventListener('pointermove', e => {
      const p = pics[i]; if (!p || closing) return;
      if (e.pointerType === 'mouse' && p.z > 1 && (mode === null || mode === 'follow')) { follow(p, e.clientX, e.clientY); paint(p, false); return; }
      if (!pts.has(e.pointerId)) return;
      pts.set(e.pointerId, [e.clientX, e.clientY]);
      if (mode === 'pinch') {
        if (pts.size < 2) return;
        const [a, b] = [...pts.values()], d = Math.hypot(a[0] - b[0], a[1] - b[1]);
        const z = Math.max(.85, Math.min(4, pinch.z * d / pinch.d)), mx = (a[0] + b[0]) / 2, my = (a[1] + b[1]) / 2, cy = midY();
        p.tx = mx - W / 2 - z * (pinch.mx - W / 2 - pinch.tx) / pinch.z;
        p.ty = my - cy - z * (pinch.my - cy - pinch.ty) / pinch.z;
        p.z = z; paint(p, false); if (z > 1) sharpen(p);
        return;
      }
      trail.push([performance.now(), e.clientX, e.clientY]); if (trail.length > 12) trail.shift();
      const dx = e.clientX - x0, dy = e.clientY - y0;
      if (mode === 'wait') {
        if (Math.hypot(dx, dy) < 8) return;
        mode = p.z > 1 ? 'pan' : Math.abs(dx) >= Math.abs(dy) ? 'x' : 'y';
        lb.classList.add('is-dragging');
      }
      if (mode === 'x') {
        const edge = (i === 0 && dx > 0) || (i === pics.length - 1 && dx < 0);
        setTrack(-i * W + (edge ? dx / 3 : dx), false);
      } else if (mode === 'y') {
        const k = Math.min(1, Math.abs(dy) / (H * .55));
        p.box.style.transition = 'none';
        p.box.style.transform = `translate3d(${dx * .5}px,${dy}px,0) scale(${1 - k * .22})`;
        lb.style.setProperty('--lb-fade', 1 - k);
      } else if (mode === 'pan') {
        p.tx = pan.tx + dx; p.ty = pan.ty + dy; clamp(p); paint(p, false);
      }
    });
    const up = e => {
      if (!pts.has(e.pointerId)) return;
      pts.delete(e.pointerId);
      const p = pics[i], dx = e.clientX - x0, dy = e.clientY - y0, [vx, vy] = speed();
      if (mode === 'pinch') {
        if (pts.size) return;   // wait for the last finger
        lb.classList.remove('is-dragging'); mode = null;
        if (p.z <= 1.05) reset(p); else { clamp(p); paint(p, true); }
        return;
      }
      lb.classList.remove('is-dragging');
      if (mode === 'x') {
        const flick = Math.abs(vx) > .4, far = Math.abs(dx) > W * .16;
        go(dx < 0 && ((flick && vx < 0) || far) ? i + 1 : dx > 0 && ((flick && vx > 0) || far) ? i - 1 : i);
      } else if (mode === 'y') {
        if ((Math.abs(vy) > .45 && Math.sign(vy) === Math.sign(dy)) || Math.abs(dy) > H * .2) shut(Math.sign(dy));
        else { p.box.style.transition = `transform .42s ${EASE}`; p.box.style.transform = ''; lb.style.setProperty('--lb-fade', 1); }
      } else if (mode === 'follow') {
        reset(p);
      } else if (mode === 'wait') {   // a click or a tap
        // pointer capture sends every event to the stage, so ask the photo's box instead of the event target
        const r = p.box.getBoundingClientRect(), onImg = e.clientX >= r.left && e.clientX <= r.right && e.clientY >= r.top && e.clientY <= r.bottom;
        if (p.vid && onImg) flip(p);   // the video: stop, or start again
        else if (e.pointerType === 'mouse') {
          if (onImg) { p.z = ZOOM; follow(p, e.clientX, e.clientY); paint(p, true); sharpen(p); }
          else shut();
        } else {
          const now = performance.now();
          if (now - lastTap < 320 && onImg) { lastTap = 0; p.z > 1 ? reset(p) : zoomAt(p, ZOOM, e.clientX, e.clientY); }
          else if (!onImg && p.z === 1) shut();
          else lastTap = now;
        }
      }
      mode = null;
    };
    stage.addEventListener('pointerup', up); stage.addEventListener('pointercancel', up);
    // trackpads: a sideways swipe browses, once per gesture
    stage.addEventListener('wheel', e => {
      e.preventDefault();
      clearTimeout(wheelT); wheelT = setTimeout(() => { wheelSum = 0; wheelLock = false; }, 160);
      if (wheelLock || Math.abs(e.deltaX) < Math.abs(e.deltaY) || pics[i].z > 1) return;
      wheelSum += e.deltaX;
      if (Math.abs(wheelSum) > 40) { wheelLock = true; go(i + Math.sign(wheelSum)); }
    }, { passive: false });
  }

  // clicks on a photo in the product gallery open this viewer instead of the theme's zoom
  document.addEventListener('click', e => {
    const hit = e.target.closest && e.target.closest('media-gallery .media-viewer__item[data-media-type="image"]');
    if (!hit || e.button > 0 || e.metaKey || e.ctrlKey || e.shiftKey) return;
    e.preventDefault(); e.stopPropagation();
    show(hit.closest('media-gallery'), hit);
  }, true);
  // the photo is a button, not a link to the image file (2026-09-28): Enter and Space open the viewer
  document.addEventListener('keydown', e => {
    if ((e.key !== 'Enter' && e.key !== ' ') || e.altKey || e.ctrlKey || e.metaKey) return;
    const a = e.target instanceof Element && e.target.closest('media-gallery .js-zoom-link');
    const hit = a && a.closest('.media-viewer__item[data-media-type="image"]');
    if (!hit) return;
    e.preventDefault();
    show(hit.closest('media-gallery'), hit);
  });
})();
